import type { Stack } from "expo-router";
import type { ComponentProps } from "react";

type StackScreenListeners = NonNullable<
  ComponentProps<typeof Stack>["screenListeners"]
>;

/**
 * Imperative navigations (e.g. DeepLinkHandler's router.replace for
 * artist/song deep links) can land a deep screen as the ONLY route in a tab's
 * stack, leaving no anchor (index) beneath it: back has nothing to pop and
 * re-tapping the focused tab is a no-op, so the tab becomes unreachable until
 * app restart (LIS-150). `withAnchor` is ignored by REPLACE actions and
 * `unstable_settings.initialRouteName` only applies to URL-based navigation,
 * so we enforce the invariant at the navigator level instead: whenever the
 * first route of the stack is not the anchor, rebuild the state with the
 * anchor prepended, preserving the live routes (no remount of the current
 * screen).
 *
 * `needsAnchor` narrows the rule to the first routes that cannot stand alone
 * (a modal, which closes onto whatever is beneath it); without it every
 * first route gets the anchor.
 *
 * Usage: <Stack screenListeners={ensureStackAnchorListeners("index")} ...>
 */
export const ensureStackAnchorListeners =
  (
    anchorRouteName: string,
    needsAnchor?: (firstRouteName: string) => boolean,
  ): StackScreenListeners =>
  ({ navigation }) => ({
    state: () => {
      const state = navigation.getState();
      if (!state || state.type !== "stack") return;
      if (state.routes.length === 0) return;
      if (state.routes[0].name === anchorRouteName) return;
      if (needsAnchor && !needsAnchor(state.routes[0].name)) return;

      console.log(
        "::ensureStackAnchor:: stack missing anchor, prepending",
        anchorRouteName,
        state.routes.map((r) => r.name),
      );

      navigation.reset({
        ...state,
        routes: [
          // A key-less route is valid in reset() — react-navigation generates
          // the key — but the NavigationState type requires one, hence the cast.
          { name: anchorRouteName } as (typeof state.routes)[number],
          ...state.routes,
        ],
        index: state.index + 1,
      });
    },
  });
